export type MangopayIdvSessionStatus = 'PENDING' | 'VALIDATED' | 'REFUSED' | 'REVIEW' | 'OUT_OF_DATE' | 'TIMEOUT' | 'ERROR';

export type MangopayIdvPscSessionStatus = 'PENDING_VALIDATION' | 'VALIDATED' | 'REJECTED' | 'ABANDONED';

// Persona de control (PSC) de una sesión IDV, solo en usuarios jurídicos
export interface MangopayIdvPscSession {
    pscId: string;
    pscType?: string;
    status: MangopayIdvPscSessionStatus;
    hostedUrl?: string;
}

export interface MangopayIdvSession {
    status: MangopayIdvSessionStatus;
    pscSessions?: MangopayIdvPscSession[];
}

// La sesión puede estar VALIDATED con una PSC que aún tiene que completar su propia verificación:
// hasta entonces Mangopay no da el KYC REGULAR y no se crea el wallet
export function getPendingPscSession(session: MangopayIdvSession): MangopayIdvPscSession | undefined {
    return session?.pscSessions?.find((psc) => psc.status === 'PENDING_VALIDATION' && !!psc.hostedUrl);
}

// PENDING incluido: tras un rechazo, al pulsar "Continuar" se crea una sesión nueva y, si no la termina, es la
// que queda como última
const IDV_RETRY_STATUSES: MangopayIdvSessionStatus[] = ['PENDING', 'REFUSED', 'OUT_OF_DATE', 'TIMEOUT', 'ERROR'];

// Última sesión IDV sin terminar, rechazada o caducada y wallet sin activar: tiene que verificar su identidad.
// También la jurídica con la PSC pendiente: la empresa queda en REVIEW o VALIDATED hasta que la PSC hace su selfie.
// Al reintentarlo, el back reutiliza la sesión si sigue en PENDING en Mangopay, devuelve el enlace de la PSC pendiente
// o crea una nueva
export function mustRetryIdentityVerification(profile: Profile): boolean {
    return profile?.walletstatus !== 0
        && (IDV_RETRY_STATUSES.includes(profile?.mangopayIdvSession?.status) || !!getPendingPscSession(profile?.mangopayIdvSession));
}

// Renovación continua del KYC en Mangopay: unos días antes de la fecha límite se avisa (UPCOMING) y, pasada,
// Mangopay rechaza ingresos, inversiones y retiradas hasta que vuelve a verificar su identidad (EXPIRED)
export type KycRenewalStatus = 'NOT_REQUIRED' | 'UPCOMING' | 'EXPIRED';

export function isKycRenewalExpired(profile: Profile): boolean {
    return profile?.kycRenewalStatus === 'EXPIRED';
}

export function mustRenewKyc(profile: Profile): boolean {
    return profile?.kycRenewalStatus === 'UPCOMING' || profile?.kycRenewalStatus === 'EXPIRED';
}

export interface RegisterPersonMainData {
    acceptConditions?: boolean;
    acceptOffers?: boolean;
    acceptWhatsapp?: boolean;
    email?: string;
    pwd?: string;
    phone?: any;
    prefix?: any;
    code?: any;
    birthDate?: any;
    name?: string;
    surname?: string;
    nation?: any;
    residence?: any;
    docType?: any;
    docNumber?: any;
    taxAddress?: any;
    civilStatus?: any;
    mobile?: any;
}

export interface RegisterCompanyMainData {
    acceptConditions?: boolean;
    acceptOffers?: boolean;
    email?: string;
    pwd?: string;
    phone?: any;
    prefix?: any;
    mobile?: any;
    code?: any;

    name?: string;
    surname?: string;
    birthDate?: any;
    businessPower?: boolean;

    socialDenomination?: string;
    nif?: string;
    cnae?: string;
    web?: string;

    taxAddress?: string;
    taxPostalCode?: string;
    taxCountry?: number;
    taxCity?: number;
    taxInternationalCity?: string;
    taxRegion?: number;
    taxInternationalRegion?: string;

    constitutionDocument1?: any; // DOC
    constitutionDocument2?: any; // DOC

    realOwnershipDocument?: any; // DOC

    enterpriseModel200?: any; // DOC

    identityDocumentPartner1?: any; // DOC
    identityDocumentPartner2?: any; // DOC
    identityDocumentPartner3?: any; // DOC
    identityDocumentPartner4?: any; // DOC

    nameRepresentative?: string;
    surnameRepresentative?: string;
    birthDateRepresentative?: any;
    actAsRepresentative?: number;

    nationalityRepresentative?: number;
    countryRepresentative?: number;

    identityDocumentTypeRepresentative?: number;

    identityDocumentDniRepresentative?: string;
    identityDocumentNieRepresentative?: string;
    identityDocumentPassportRepresentative?: string;

    identityDocumentFrontRepresentative?: string;
    identityDocumentBackRepresentative?: string;

    identityDocumentFrontNieRepresentative?: string;
    identityDocumentBackNieRepresentative?: string;

    identityDocumentFrontPassportRepresentative?: string;

    addressVerifyRepresentative?: string;

    accountAcreditation1000kActive?: boolean;
    accountAcreditation2000kBusiness?: boolean;
    accountAcreditation300kResources?: boolean;
    accountAcreditationProfessionalAdvice?: boolean;

    acceptPaymentEntity?: boolean;

    addressRepresentative?: any;
    cityRepresentative?: any;
    internationalCityRepresentative?: any;
    internationalRegionRepresentative?: any;
    postalCodeRepresentative?: any;
    regionRepresentative?: any;
    taxRegregionRepresentative?: any;

    partners?: any;
    shareholderDeclarations?: any;
}

export interface ProfileMainData {
    civilStatus?: any;
    taxCountry?: any;
    nationality?: any;
    birthDate?: string;
    username?: any;
    taxAddress?: any;
    name?: any;
    surname?: any;
}
export interface RegisterDocument {
    frontDocument?: any;
    backDocument?: any;
    frontNieDocument?: any;
    backNieDocument?: any;
    frontPassportDocument?: any;
}
export interface RegisterPersonSteps {
    steps: [
        { name: 'emailUser', index: number }?,
        { name: 'nameUser', index: number }?,
        { name: 'phoneUser', index: number }?,
        { name: 'code', index: number }?,
        { name: 'nationalityUser', index: number }?,
        { name: 'messageFile', index: number }?,
        { name: 'docTypeNumber', index: number }?,
    ];
}
export enum UserAddressType {
    HABITUAL_RESIDENCE = 1,
    OWNED_PROPERTY = 2,
    RENTED_PROPERTY = 3,
    WORKPLACE = 4,
    TAX_ADDRESS = 5
}

export interface UserAddress {
    id?: number;
    types: UserAddressType[];
    address: string;
    postalCode: string;
    city: string;
    region: string;
    country: number;
    internationalCity?: string;
    internationalRegion?: string;
    isVerified?: boolean;
    verificationStatus?: 'pending' | 'verified' | 'rejected';
    createdAt?: string;
    updatedAt?: string;
}

// 1 = Dirección del DNI
// 2 = Dirección censal
// 3 = Dirección de residencia
// 4 = Vivienda en propiedad
// 5 = Vivienda de alquiler
// 6 = Dirección laboral
export interface ExtraAddress {
    id?: number;
    type?: number;
    address?: string;
    country?: number;
    region?: string;
    city?: string;
    postalCode?: string;
    internationalRegion?: string;
    internationalCity?: string;
}

export interface Profile {
    acceptCommunications?: boolean;
    acceptPaymentEntity?: boolean;
    accountAcreditation50kIncomings?: boolean;
    accountAcreditation100kWealth?: boolean;
    accountAcreditation300kResources?: boolean;
    accountAcreditation1000kActive?: boolean;
    accountAcreditation2000kBusiness?: boolean;
    accountAcreditationProfessionalAdvice?: boolean;
    acreditationSignedIfInProgress?: boolean;
    acreditatedInvestor?: boolean;
    acreditationStatus?: string;
    actAsRepresentative?: any;
    addressRepresentative?: any;
    addressVerifyRepresentative?: any;
    acreditationVerificationIrpfOrContract?: string;
    acreditationVerificationBankInfo?: string;
    acreditationVerificationFinancialAdvice?: string;
    acreditationVerificationBalanceSheet?: string;
    acreditationVerificationProfitLosses?: string;
    annualGrossIncome?: any;
    annualInvestmentActivities?: any;
    bankAccountVerificationDocument?: any;
    birthDate?: any;
    birthDateRepresentative?: any;
    businessPower?: boolean;
    balance?: any;
    cityRepresentative?: any;
    civilStatus?: number;
    cnae?: any;
    companyName?: string;
    companyNIF?: any;
    companyAddress?: string;
    companyHome?: string;
    companyPostalCode?: any;
    pendingEmailVerification?: {
        email: string;
        expirationDate: string;
    };
    companyCity?: string;
    companyRegion?: string;
    constitutionDocument1?: any;
    constitutionDocument2?: any;
    convenienceTestLoan?: boolean;
    convenienceTestShares?: boolean;
    countryRepresentative?: any;
    creationDate?: any;
    dontRequestInvestmentExperienceInfo?: any;
    enterpriseModel200?: any;
    extraPostalCodes?: any;
    finantialPatrimony?: any;
    files?: any;
    gender?: number;
    genderRepresentative?: any;
    hasExperienceInFinantialSector?: any;
    howDidYouMeetUs?: any;
    iban?: string;
    identityDocumentBack?: any;
    identityDocumentBackRepresentative?: any;
    identityDocumentFront?: string;
    identityDocumentFrontRepresentative?: any;
    identityDocumentNumber?: string;
    identityDocumentNumberRepresentative?: any;
    identityDocumentPartner1?: any;
    identityDocumentPartner2?: any;
    identityDocumentPartner3?: any;
    identityDocumentPartner4?: any;
    identityDocumentType?: number;
    identityDocumentTypeRepresentative?: any;
    identityDocumentFrontNie?: any;
    identityDocumentBackNie?: any;
    identityDocumentFrontPassport?: any;
    identityDocumentDniRepresentative?: string;
    identityDocumentNieRepresentative?: string;
    identityDocumentPassportRepresentative?: string;
    identityDocumentFrontNieRepresentative?: string;
    identityDocumentBackNieRepresentative?: string;
    identityDocumentFrontPassportRepresentative?: string;
    internationalCityRepresentative?: any;
    internationalRegionRepresentative?: any;
    investmentMaximumLast12Months?: number;
    investmentMaximumLoan?: number;
    investmentMaximumPercentage?: any;
    investmentMaximumShares?: number;
    investmentMinimum?: number;
    lemonwayKycStatus?: number;
    mangopayConditionsAccepted?: boolean;
    mangopayKycStatus?: number;
    kycRenewalStatus?: KycRenewalStatus;
    kycRenewalDeadline?: string; // yyyy-MM-dd
    mangopayWalletId?: any;
    isMangopayScaContactInformationUpdateConsent?: boolean;
    isMangopayScaRecipientRegistrationConsent?: boolean;
    isMangopayScaTransferConsent?: boolean;
    isMangopayScaViewAccountInformationConsent?: boolean;
    name?: string;
    nameRepresentative?: any;
    nationality?: number;
    nationalityRepresentative?: any;
    nif?: any;
    phone?: any;
    partners?: any;
    positionRepresentative?: any;
    postalCodeRepresentative?: any;
    profession?: string;
    profileImage?: any;
    profileImageFormat?: any;
    profileType?: number;
    realOwnershipDocument?: any;
    reintegroSaul?: any;
    referralCode?: string;
    regionRepresentative?: any;
    showInvestments?: boolean;
    socialDenomination?: any;
    surname?: string;
    surnameRepresentative?: any;
    taxAddress?: string;
    taxAddressVerificationDocument?: any;
    taxCity?: any;
    taxCountry?: number;
    taxInternationalCity?: string;
    taxInternationalRegion?: string;
    taxPostalCode?: string;
    taxRegion?: any;
    taxRegregionRepresentative?: any;
    turnover?: any;
    username?: string;
    walletid?: string;
    walletstatus?: number;
    web?: any;
    mobileVerified?: boolean;
    mailVerified?: boolean;
    identityDocumentPassport?: any;
    identityDocumentNie?: any;
    identityDocumentDni?: any;
    mobile?: any;
    isExperiencedInvestor?: boolean;
    mangopayBankingAliasBic?: string;
    mangopayBankingAliasIban?: string;
    mangopayFundsMigrationStatus?: any;
    mangopayUserId?: any;
    mpUserCategory?: number; // 0 -> Payer y 1 -> Owner
    ownResources?: any;
    shareholderDeclarations?: any;
    addresses?: ExtraAddress[];
    mpUboDeclarationId?: any;
    mpUboDeclarationStatus?: any;
    isEnrolledSca?: boolean;
    mangopayIdvSession?: MangopayIdvSession;
    mpUboDeclarationReasonMsg?: any;
    createEnterpriseOwnerWallet?: any;
    createOwnerWallet?: any;
}
